class Pagination extends HTMLElement {
  constructor() {
    super();

    this.LOADING_CLASS = "button--loading";

    this.loading = false;

    if (!this.trigger) return;

    this.triggerType === "button" ? this.addClickListener() : this.addObserver();
  }

  /**
   * Adds click event listener for button trigger.
   */
  addClickListener() {
    this.trigger.addEventListener("click", () => {
      if (!this.loading) this.getSection(this.nextPage);
    });
  }

  /**
   * Adds intersection observer for infinite scroll trigger.
   */
  addObserver() {
    const options = { root: null, rootMargin: "100px", threshold: 0.25 };

    this.observer = new IntersectionObserver(this.handleIntersect.bind(this), options);
    this.observer.observe(this.trigger);
  }

  /**
   * Handles intersection observer events.
   * @param {IntersectionObserverEntry[]} entries
   */
  handleIntersect(entries) {
    entries.forEach(entry => {
      if (this.nextPage > this.totalPages) {
        return this.observer.unobserve(entry.target);
      }

      if (entry.isIntersecting && !this.loading) {
        this.getSection(this.nextPage);
      }
    });
  }

  /**
   * Fetches and updates the next page section.
   */
  getSection() {
    const pageUrl = this.getPageUrl(this.nextPage);
    const section = this.getSectionUrl(pageUrl);

    this.loading = true;
    this.trigger.classList.add(this.LOADING_CLASS);

    fetch(section)
      .then(response => response.text())
      .then(data => this.updateSection(data))
      .finally(() => {
        this.loading = false;
        this.trigger.classList.remove(this.LOADING_CLASS);
      });
  }

  /**
   * Returns the URL for the next page.
   * @param {number} page
   * @returns {string}
   */
  getPageUrl(page) {
    const path = window.location.href;
    const url = symbol => path + symbol + 'page=' + page;

    return path.includes('page=')
      ? this.setSearchParam(path, page)
      : path.includes('?')
        ? url('&')
        : url('?');
  }

  /**
   * Sets the search param for page in the URL.
   * @param {string} path
   * @param {number} page
   * @returns {string}
   */
  setSearchParam(path, page) {
    this.url.searchParams.set('page', page.toString());
    this.url.search = this.url.searchParams.toString();

    return this.url.toString();
  }

  /**
   * Returns the section URL for pagination.
   * @param {string} page
   * @returns {string}
   */
  getSectionUrl(page) {
    return page + '&section_id=' + this.sectionId;
  }

  /**
   * Updates the DOM with new items and pagination.
   * @param {string} section
   */
  updateSection(section) {
    const dataHtml = new DOMParser().parseFromString(section, "text/html");
    const containers = dataHtml.querySelectorAll("[data-results-grid]");
    const pagination = dataHtml.querySelector(this.paginationSelector);

    this.addNextPageItems(containers, pagination);
  }

  /**
   * Appends new items and replaces pagination.
   * @param containers
   * @param {HTMLElement} pagination
   */
  addNextPageItems(containers, pagination) {
    containers.forEach((container, index) => {
      const items = container.children;
      const newContainer = this.itemsContainers[index];

      while (items.length > 0) newContainer.append(items[0]);
    });

    document.querySelector(this.paginationSelector).replaceWith(pagination);
  }

  /**
   * @returns {string} Pagination selector
   */
  get paginationSelector() {
    return "[data-pagination]";
  }

  /**
   * @returns {number} Next page number
   */
  get nextPage() {
    return Number(this.dataset.nextPage);
  }

  /**
   * @returns {number} Total number of pages
   */
  get totalPages() {
    return Number(this.dataset.total);
  }

  /**
   * @returns {HTMLElement} Items container
   */
  get itemsContainers() {
    return document.querySelectorAll("[data-results-grid]");
  }

  /**
   * @returns {HTMLElement} Trigger element
   */
  get trigger() {
    return this.querySelector("[data-trigger]");
  }

  /**
   * @returns {string} Trigger type
   */
  get triggerType() {
    return this.dataset.triggerType;
  }

  /**
   * @returns {URL} Current page URL object
   */
  get url() {
    return new URL(window.location.href);
  }

  /**
   * @returns {string} Section ID for pagination
   */
  get sectionId() {
    return this.getAttribute("data-pagination-section");
  }
}

customElements.define('infinite-pagination', Pagination);